import MungPayUtil from '../../../core/utils/LogUtil';
import { IMungPayRepository } from '../interfaces';
import axios from 'axios';

/**
 * MungPay Repository Implementation
 * 인터페이스 구현체, 실제 API 호출 담당
 * ApiClient 인프라에 의존
 */
export class MungPayRepositoryImpl extends IMungPayRepository {
  constructor(apiClient) {
    super();
    if (!apiClient) {
      throw new Error('ApiClient is required');
    }
    this.apiClient = apiClient;
  }

  /**
   * 보험 청구 생성
   * POST /mungpay/claims
   */
  async createClaim(url) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - 보험 청구 생성');
      MungPayUtil.log('📄 [MungPay Repository] 입력 데이터:', url);

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.post('/mungpay/claims', {
        medical_receipt_url: url,
      });

      MungPayUtil.log('✅ [MungPay Repository] API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Repository] API 호출 실패:', error);

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('요청한 리소스를 찾을 수 없습니다');
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(`API 호출 실패 (${status}): ${message}`);
        }
      }

      throw new Error(`청구 생성 실패: ${error.message}`);
    }
  }

  /**
   * 전화번호 인증
   * POST /mungpay/claims/{id}/phone-verify
   */
  async verifyPhone(claimId, phoneNumber) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - 전화번호 인증');
      MungPayUtil.log('📊 [MungPay Repository] 청구 ID:', claimId);
      MungPayUtil.log('📄 [MungPay Repository] 전화번호 데이터:', phoneNumber);

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.post(
        `/mungpay/claims/${claimId}/phone-verify`,
        { phone_number: phoneNumber, model_name: 'claude-3.5' }
      );

      MungPayUtil.log('✅ [MungPay Repository] 전화번호 인증 API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] 전화번호 인증 API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('청구를 찾을 수 없습니다');
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(`API 호출 실패 (${status}): ${message}`);
        }
      }

      throw new Error(`전화번호 인증 실패: ${error.message}`);
    }
  }

  /**
   * 청구 상태 조회
   * GET /mungpay/claims/number/{claimNumber}/status
   */
  async getClaimStatus(claimNumber) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - 청구 상태 조회');
      MungPayUtil.log('📊 [MungPay Repository] 청구 번호:', claimNumber);

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.get(
        `/mungpay/claims/number/${claimNumber}/status`
      );

      MungPayUtil.log('✅ [MungPay Repository] 청구 상태 조회 API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] 청구 상태 조회 API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('청구를 찾을 수 없습니다');
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(`API 호출 실패 (${status}): ${message}`);
        }
      }

      throw new Error(`청구 상태 조회 실패: ${error.message}`);
    }
  }

  /**
   * 청구 결과 조회
   * GET /mungpay/claims/{id}/result
   */
  async getClaimResult(claimId) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - 청구 결과 조회');
      MungPayUtil.log('📊 [MungPay Repository] 청구 ID:', claimId);

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.get(
        `/mungpay/claims/number/${claimId}/result`
      );

      MungPayUtil.log('✅ [MungPay Repository] 청구 결과 조회 API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] 청구 결과 조회 API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('청구 결과를 찾을 수 없습니다');
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(`API 호출 실패 (${status}): ${message}`);
        }
      }

      throw new Error(`청구 결과 조회 실패: ${error.message}`);
    }
  }

  /**
   * S3 파일 업로드 (Presigned URL 방식)
   * GET /s3/mungpay/presigned-url?file_name={fileName}&content_type={contentType}&expiration={expiration}
   * 그 후 S3에 직접 업로드
   */
  async uploadToS3(file, expiration = '60') {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] S3 파일 업로드 시작');
      MungPayUtil.log('📄 [MungPay Repository] 파일명:', file.name);
      MungPayUtil.log('📄 [MungPay Repository] 콘텐츠 타입:', file.type);
      MungPayUtil.log('📄 [MungPay Repository] 파일 크기:', file.size);
      MungPayUtil.log('📄 [MungPay Repository] 만료 시간:', expiration);

      // Step 1: Presigned URL 요청
      const params = new URLSearchParams({
        file_name: file.name,
        content_type: file.type,
        expiration: expiration,
      });

      const response = await this.apiClient.get(
        `/s3/mungpay/presigned-url?${params}`
      );

      MungPayUtil.log('✅ [MungPay Repository] S3 Presigned URL API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      if (response.result_code !== 200) {
        throw new Error(response.result_msg || '업로드 URL 생성 실패');
      }

      MungPayUtil.log(
        '🔗 [MungPay Repository] Presigned URL 획득:',
        response.data.presigned_url
      );

      MungPayUtil.log('🔗 [MungPay Repository] file 획득:', file);

      MungPayUtil.log('🔗 [MungPay Repository] content-type 획득:', file.type);
      // Step 2: S3에 직접 파일 업로드presigned_url
      MungPayUtil.log('📤 [MungPay Repository] S3에 파일 업로드 시작');
      const uploadResponse = await axios.put(
        response.data.presigned_url,
        file,
        {
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'public-read', // S3에 업로드 시 공개 읽기 권한 설정
          },
        }
      );
      MungPayUtil.log(
        '📤 [MungPay Repository] S3 업로드 응답 상태:',
        uploadResponse.status
      );

      MungPayUtil.log('✅ [MungPay Repository] S3 파일 업로드 성공');

      // Step 3: 성공 시 접근 가능한 URL 반환
      const uploadResult = {
        success: true,
        imageUrl: response.data.full_url,
        fileKey: response.data.file_key,
        message: '업로드 성공',
      };

      MungPayUtil.log(
        '📄 [MungPay Repository] 최종 업로드 결과:',
        uploadResult
      );

      return uploadResult;
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] S3 Presigned URL API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('요청한 리소스를 찾을 수 없습니다');
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(`API 호출 실패 (${status}): ${message}`);
        }
      }

      throw new Error(`S3 Presigned URL 요청 실패: ${error.message}`);
    }
  }

  /**
   * KB 영수증 분석
   * POST /mungpay/kb_receipt_analyze
   */
  async analyzeKbReceipt(medicalReceiptUrl) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - KB 영수증 분석');
      MungPayUtil.log('📄 [MungPay Repository] 입력 데이터:', {
        medical_receipt_url: medicalReceiptUrl,
      });

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.post(
        '/mungpay/kb_receipt_analyze',
        {
          medical_receipt_url: medicalReceiptUrl,
        }
      );

      MungPayUtil.log('✅ [MungPay Repository] API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] KB 영수증 분석 API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('요청한 리소스를 찾을 수 없습니다');
          case 422:
            throw new Error(`처리할 수 없는 요청: ${message}`);
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(
              `KB 영수증 분석 API 호출 실패 (${status}): ${message}`
            );
        }
      }

      throw new Error(`KB 영수증 분석 요청 실패: ${error.message}`);
    }
  }

  /**
   * 처방전 분석
   * POST /mungpay/prescription_analyze
   */
  async analyzePrescription(prescriptionUrl) {
    try {
      MungPayUtil.log('🌐 [MungPay Repository] API 호출 - 처방전 분석');
      MungPayUtil.log('📄 [MungPay Repository] 입력 데이터:', {
        prescription_url: prescriptionUrl,
      });

      // API 호출 - BaseResponse 그대로 반환
      const response = await this.apiClient.post(
        '/mungpay/prescription_analyze',
        {
          prescription_url: prescriptionUrl,
        }
      );

      MungPayUtil.log('✅ [MungPay Repository] API 응답 성공');
      MungPayUtil.log('📊 [MungPay Repository] 응답 타입:', typeof response);
      MungPayUtil.log('📄 [MungPay Repository] 응답 데이터:', response);

      return response; // BaseResponse<any> 그대로 반환
    } catch (error) {
      MungPayUtil.error(
        '❌ [MungPay Repository] 처방전 분석 API 호출 실패:',
        error
      );

      // HTTP 상태코드별 에러 처리
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 400:
            throw new Error(`잘못된 요청: ${message}`);
          case 401:
            throw new Error('인증이 필요합니다');
          case 403:
            throw new Error('접근 권한이 없습니다');
          case 404:
            throw new Error('요청한 리소스를 찾을 수 없습니다');
          case 422:
            throw new Error(`처리할 수 없는 요청: ${message}`);
          case 500:
            throw new Error('서버 내부 오류가 발생했습니다');
          default:
            throw new Error(
              `처방전 분석 API 호출 실패 (${status}): ${message}`
            );
        }
      }

      throw new Error(`처방전 분석 요청 실패: ${error.message}`);
    }
  }
}
